import { useCallback, useEffect, useMemo, useState } from "react";
import Sidebar from "../../../../components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { useAuth } from "../../../../context/AuthContext";
import Swal from "sweetalert2";
import {
  DesperfectoRecord,
  EstatusDesperfecto,
  obtenerDesperfectos,
  obtenerEstatusDesperfecto,
  responderDesperfecto,
  cerrarDesperfecto,
} from "../../../../api/desperfectoService";
import {
  Wrench,
  RefreshCcw,
  Search,
  ShieldAlert,
  User,
  Clock,
  Hammer,
  Eye,
  CheckCircle,
} from "lucide-react";

const ALLOWED_ROLES = ["AD", "AS"];

interface NormalizedStatus {
  code: string;
  name: string;
}

const getStatusStyle = (status?: string) => {
  const key = (status || "").trim().charAt(0).toUpperCase();
  if (key === "A") {
    return "text-orange-600 bg-orange-50 border border-orange-200";
  }
  if (key === "C") {
    return "text-green-600 bg-green-50 border border-green-200";
  }
  if (key === "P") {
    return "text-blue-600 bg-blue-50 border border-blue-200";
  }
  return "text-gray-600 bg-gray-50 border border-gray-200";
};

const normalizeStatus = (status: EstatusDesperfecto): NormalizedStatus => ({
  code:
    status.ede_codigo ||
    status.des_estatus ||
    status.codigo ||
    "",
  name:
    status.ede_nombre ||
    status.des_estatus_nombre ||
    status.nombre ||
    "Sin nombre",
});

export default function DesperfectoListPage() {
  const { session } = useAuth();
  const unidadCodigo = session?.unidad?.id || "";
  const rolCodigo = (session?.rol?.id || "").toUpperCase();
  const isAuthorized = ALLOWED_ROLES.includes(rolCodigo);

  const [isLoading, setIsLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [desperfectos, setDesperfectos] = useState<DesperfectoRecord[]>([]);
  const [statuses, setStatuses] = useState<NormalizedStatus[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>("A");
  const [searchTerm, setSearchTerm] = useState("");
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [selectedDesperfecto, setSelectedDesperfecto] = useState<DesperfectoRecord | null>(null);
  const [responseValue, setResponseValue] = useState("");
  const [actionLoading, setActionLoading] = useState<"save" | "close" | null>(null);

  const fetchStatuses = useCallback(async () => {
    try {
      setStatusLoading(true);
      const response = await obtenerEstatusDesperfecto();
      if (response.code === "1" && Array.isArray(response.object_)) {
        const normalized = response.object_
          .map(normalizeStatus)
          .filter((status) => status.code);
        setStatuses(normalized);
      } else {
        setStatuses([]);
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "No se pudieron cargar los estatus";
      await Swal.fire({
        icon: "error",
        title: "Error al cargar estatus",
        text: message,
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const fetchDesperfectos = useCallback(
    async (silent = false) => {
      if (!unidadCodigo || !isAuthorized) return;

      if (!silent) {
        setIsLoading(true);
      }

      try {
        const payload = {
          codigo: "",
          "unidad residencial codigo": unidadCodigo,
          "usuario que reporto daño": "",
          "estatus codigo": selectedStatus || "",
          "fecha inicial": "",
          "fecha final": "",
        };

        const response = await obtenerDesperfectos(payload);

        if (response.code === "1" && Array.isArray(response.object_)) {
          setDesperfectos(response.object_);
          setSelectedDesperfecto((prev) => {
            if (!prev) return null;
            const stillExists = response.object_.find(
              (item) => item.des_codigo === prev.des_codigo
            );
            return stillExists || null;
          });
        } else {
          setDesperfectos([]);
          setSelectedDesperfecto(null);
        }

        setLastUpdated(new Date());
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "No se pudieron cargar los desperfectos";
        await Swal.fire({
          icon: "error",
          title: "Error al cargar desperfectos",
          text: message,
          confirmButtonColor: "#dc2626",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [unidadCodigo, selectedStatus, isAuthorized]
  );

  useEffect(() => {
    if (isAuthorized) {
      fetchStatuses();
    }
  }, [fetchStatuses, isAuthorized]);

  useEffect(() => {
    if (unidadCodigo && isAuthorized) {
      fetchDesperfectos();
    }
  }, [unidadCodigo, isAuthorized, fetchDesperfectos]);

  useEffect(() => {
    if (selectedDesperfecto) {
      setResponseValue(selectedDesperfecto.des_respuesta || "");
    } else {
      setResponseValue("");
    }
  }, [selectedDesperfecto]);

  const filteredDesperfectos = useMemo(() => {
    if (!searchTerm.trim()) {
      return desperfectos;
    }
    const term = searchTerm.toLowerCase();
    return desperfectos.filter((item) => {
      const fields = [
        item.des_descripcion,
        item.des_usuario,
        item.des_tipo_usuario_nombre,
        item.des_estatus_nombre,
      ];
      return fields.some((field) => (field || "").toLowerCase().includes(term));
    });
  }, [desperfectos, searchTerm]);

  const summary = useMemo(() => {
    const abiertos = desperfectos.filter(
      (item) => (item.des_estatus || "").toUpperCase() === "A"
    ).length;
    const cerrados = desperfectos.filter(
      (item) => (item.des_estatus || "").toUpperCase() === "C"
    ).length;
    return {
      total: desperfectos.length,
      abiertos,
      cerrados,
    };
  }, [desperfectos]);

  const handleSaveResponse = async () => {
    if (!selectedDesperfecto) return;

    const trimmed = responseValue.trim();
    if (!trimmed) {
      await Swal.fire({
        icon: "info",
        title: "Respuesta requerida",
        text: "Ingresa una respuesta para guardar.",
        confirmButtonColor: "#3d5a40",
      });
      return;
    }

    try {
      setActionLoading("save");
      await responderDesperfecto({
        codigo: selectedDesperfecto.des_codigo.toString(),
        respuesta: trimmed,
      });
      setSelectedDesperfecto((prev) =>
        prev
          ? {
              ...prev,
              des_respuesta: trimmed,
              des_estatus_nombre: "En proceso",
              des_estatus: "P",
            }
          : prev
      );
      setDesperfectos((prev) =>
        prev.map((item) =>
          item.des_codigo === selectedDesperfecto.des_codigo
            ? {
                ...item,
                des_respuesta: trimmed,
                des_estatus_nombre: "En proceso",
                des_estatus: "P",
              }
            : item
        )
      );
      await fetchDesperfectos(true);
      await Swal.fire({
        icon: "success",
        title: "Respuesta guardada",
        text: "El desperfecto pasó a estado En proceso.",
        confirmButtonColor: "#3d5a40",
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "No se pudo guardar la respuesta";
      await Swal.fire({
        icon: "error",
        title: "Error al guardar",
        text: message,
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCloseDesperfecto = async () => {
    if (!selectedDesperfecto) return;

    const confirmation = await Swal.fire({
      icon: "warning",
      title: "¿Cerrar este desperfecto?",
      text: "Esta acción marcará el caso como resuelto.",
      showCancelButton: true,
      confirmButtonText: "Sí, cerrar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#16a34a",
      cancelButtonColor: "#6b7280",
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    try {
      setActionLoading("close");
      await cerrarDesperfecto({
        codigo: selectedDesperfecto.des_codigo.toString(),
      });
      const closureDate =
        selectedDesperfecto.des_fecha_cierre || new Date().toLocaleString();
      setSelectedDesperfecto((prev) =>
        prev
          ? {
              ...prev,
              des_estatus_nombre: "Cerrado",
              des_estatus: "C",
              des_fecha_cierre: closureDate,
            }
          : prev
      );
      setDesperfectos((prev) =>
        prev.map((item) =>
          item.des_codigo === selectedDesperfecto.des_codigo
            ? {
                ...item,
                des_estatus_nombre: "Cerrado",
                des_estatus: "C",
                des_fecha_cierre: closureDate,
              }
            : item
        )
      );
      await fetchDesperfectos(true);
      await Swal.fire({
        icon: "success",
        title: "Desperfecto cerrado",
        text: "El caso fue marcado como solucionado.",
        confirmButtonColor: "#3d5a40",
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "No se pudo cerrar el desperfecto";
      await Swal.fire({
        icon: "error",
        title: "Error al cerrar",
        text: message,
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setActionLoading(null);
    }
  };

  if (!isAuthorized) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <Sidebar />
        <main className="flex-1 p-6 md:ml-64 flex items-center justify-center">
          <Card className="max-w-lg shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <ShieldAlert size={24} />
                Acceso restringido
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Esta sección está disponible únicamente para administradores (AD) y admin socio (AS).
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 text-gray-900">
      <Sidebar />
      <main className="flex-1 p-4 sm:p-6 md:ml-64 transition-all space-y-4 sm:space-y-6">
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-military-600">
            Reporte de daños
          </h1>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
            {lastUpdated && (
              <span className="text-xs sm:text-sm text-gray-500">
                Actualizado: {lastUpdated.toLocaleString()}
              </span>
            )}
            <Button
              variant="outline"
              className="gap-2 w-full sm:w-auto"
              onClick={() => fetchDesperfectos()}
              disabled={isLoading}
            >
              <RefreshCcw size={18} className={isLoading ? "animate-spin" : ""} />
              Actualizar
            </Button>
          </div>
        </header>

        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card className="border-l-4 border-military-500 shadow-md">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total reportes</p>
                  <p className="text-3xl font-bold text-gray-900">{summary.total}</p>
                </div>
                <div className="bg-military-100 p-3 rounded-full">
                  <Hammer size={24} className="text-military-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-orange-500 shadow-md">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Abiertos</p>
                  <p className="text-3xl font-bold text-orange-600">{summary.abiertos}</p>
                </div>
                <div className="bg-orange-100 p-3 rounded-full">
                  <ShieldAlert size={24} className="text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-green-500 shadow-md">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Cerrados</p>
                  <p className="text-3xl font-bold text-green-600">{summary.cerrados}</p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <Clock size={24} className="text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <Card className="shadow-lg">
          <CardHeader className="border-b">
            <CardTitle className="text-lg font-semibold">Filtros</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-3">Estatus</p>
              <div className="flex flex-wrap gap-3">
                <label
                  className={`cursor-pointer px-4 py-2 rounded-full border text-sm font-medium ${
                    selectedStatus === ""
                      ? "bg-military-100 border-military-500 text-military-600"
                      : "border-gray-300 text-gray-600"
                  }`}
                >
                  <input
                    type="radio"
                    className="sr-only"
                    checked={selectedStatus === ""}
                    onChange={() => setSelectedStatus("")}
                  />
                  Todos
                </label>
                {statusLoading ? (
                  <span className="text-sm text-gray-500">Cargando estatus...</span>
                ) : statuses.length === 0 ? (
                  <span className="text-sm text-gray-500">Sin estatus disponibles.</span>
                ) : (
                  statuses.map((status) => (
                    <label
                      key={status.code}
                      className={`cursor-pointer px-4 py-2 rounded-full border text-sm font-medium ${
                        selectedStatus === status.code
                          ? "bg-military-100 border-military-500 text-military-600"
                          : "border-gray-300 text-gray-600"
                      }`}
                    >
                      <input
                        type="radio"
                        className="sr-only"
                        value={status.code}
                        checked={selectedStatus === status.code}
                        onChange={() => setSelectedStatus(status.code)}
                      />
                      {status.name}
                    </label>
                  ))
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <Search size={16} />
                  Buscar
                </div>
              </label>
              <Input
                placeholder="Descripción, usuario, tipo de usuario..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
          <Card className="shadow-lg xl:col-span-2">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2">
                <Hammer size={20} />
                Desperfectos ({filteredDesperfectos.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="h-12 w-12 animate-spin rounded-full border-4 border-t-military-500 border-gray-300 mb-4" />
                  <p className="text-gray-600">Cargando desperfectos...</p>
                </div>
              ) : filteredDesperfectos.length === 0 ? (
                <div className="text-center py-16">
                  <Hammer size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 font-medium">No se encontraron informes</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Ajusta los filtros o actualiza la información para ver nuevos reportes.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {filteredDesperfectos.map((item) => {
                    const isSelected = selectedDesperfecto?.des_codigo === item.des_codigo;
                    return (
                      <button
                        key={item.des_codigo}
                        onClick={() => setSelectedDesperfecto(item)}
                        className={`flex flex-col md:flex-row gap-4 p-4 border rounded-2xl bg-white shadow-sm hover:shadow-lg transition text-left focus:outline-none ${
                          isSelected ? "ring-2 ring-military-400" : ""
                        }`}
                      >
                        <div className="w-full md:w-32 h-32 rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center border">
                          {item.des_ruta_foto ? (
                            <img
                              src={item.des_ruta_foto}
                              alt={`Desperfecto ${item.des_codigo}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const target = e.currentTarget;
                                target.style.display = "none";
                              }}
                            />
                          ) : (
                            <Wrench size={36} className="text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 space-y-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-sm font-semibold text-gray-500">
                              #{item.des_codigo.toString().padStart(3, "0")}
                            </span>
                            <span
                              className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${getStatusStyle(
                                item.des_estatus
                              )}`}
                            >
                              {item.des_estatus_nombre}
                            </span>
                          </div>
                          <p className="text-gray-900 font-semibold">
                            {item.des_descripcion || "Sin descripción"}
                          </p>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p className="flex items-center gap-2">
                              <User size={14} />
                              {item.des_usuario}
                            </p>
                            <p className="flex items-center gap-2">
                              <ShieldAlert size={14} />
                              {item.des_tipo_usuario_nombre}
                            </p>
                            <p className="flex items-center gap-2">
                              <Clock size={14} />
                              {item.des_fecha_creacion}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-lg">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2">
                <Eye size={20} />
                Detalle del desperfecto
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              {!selectedDesperfecto ? (
                <div className="text-center py-12">
                  <Eye size={40} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-600">Selecciona un reporte para ver su detalle.</p>
                </div>
              ) : (
                <>
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                    <div className="w-full rounded-lg bg-white border border-dashed border-gray-300 flex items-center justify-center overflow-hidden" style={{ minHeight: "200px" }}>
                      {selectedDesperfecto.des_ruta_foto ? (
                        <img
                          src={selectedDesperfecto.des_ruta_foto}
                          alt={`Desperfecto ${selectedDesperfecto.des_codigo}`}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            const target = e.currentTarget;
                            target.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="text-center text-gray-400 flex flex-col items-center gap-2 py-8">
                          <Wrench size={36} />
                          <span>Sin imagen adjunta</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <p className="text-xs uppercase text-gray-500 font-semibold">Código</p>
                    <p className="text-2xl font-bold text-gray-900">
                      #{selectedDesperfecto.des_codigo.toString().padStart(3, "0")}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs uppercase text-gray-500 font-semibold">Reportado por</p>
                      <p className="text-sm text-gray-800">{selectedDesperfecto.des_usuario}</p>
                      <span className="text-xs text-gray-500">
                        {selectedDesperfecto.des_tipo_usuario_nombre}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-gray-500 font-semibold">Estado</p>
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${getStatusStyle(
                          selectedDesperfecto.des_estatus
                        )}`}
                      >
                        {selectedDesperfecto.des_estatus_nombre}
                      </span>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs uppercase text-gray-500 font-semibold mb-1">Descripción</p>
                    <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 border border-gray-200 min-h-[90px]">
                      {selectedDesperfecto.des_descripcion || "Sin descripción"}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs uppercase text-gray-500 font-semibold">Fecha creación</p>
                      <p className="text-sm text-gray-700">
                        {selectedDesperfecto.des_fecha_creacion || "Sin fecha"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-gray-500 font-semibold">Fecha cierre</p>
                      <p className="text-sm text-gray-700">
                        {selectedDesperfecto.des_fecha_cierre || "Sin cierre"}
                      </p>
                    </div>
                  </div>

                  {(() => {
                    const isCerrado = 
                      selectedDesperfecto.des_estatus?.toUpperCase() === "C" ||
                      selectedDesperfecto.des_estatus_nombre?.toLowerCase().includes("cerrado");
                    
                    if (isCerrado) {
                      return null;
                    }

                    return (
                      <>
                        <div className="space-y-2 border-t border-gray-200 pt-4">
                          <p className="text-xs uppercase text-gray-500 font-semibold">
                            Respuesta al residente
                          </p>
                          <Textarea
                            value={responseValue}
                            onChange={(e) => setResponseValue(e.target.value)}
                            placeholder="Escribe la respuesta que se notificará al residente..."
                            className="min-h-[140px]"
                          />
                          <p className="text-xs text-gray-500">
                            Guardar respuesta cambia el estado a <strong>En proceso</strong>.
                          </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                          <Button
                            className="flex-1 bg-military-500 hover:bg-military-600 text-white"
                            onClick={handleSaveResponse}
                            disabled={actionLoading === "save"}
                          >
                            {actionLoading === "save" ? "Guardando..." : "Guardar respuesta"}
                          </Button>
                          <Button
                            variant="destructive"
                            className="flex-1 flex items-center justify-center gap-2"
                            onClick={handleCloseDesperfecto}
                            disabled={actionLoading === "close"}
                          >
                            {actionLoading === "close" ? "Cerrando..." : "Cerrar desperfecto"}
                            {actionLoading === "close" ? null : <CheckCircle size={16} />}
                          </Button>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

